import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

// register plugins
gsap.registerPlugin(ScrollTrigger, SplitText);

export const prefersReducedMotion = () => window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

const getElements = ( root, selector ) => {
	const elements = Array.from( root.querySelectorAll( selector ) );

	if ( root.matches && root.matches( selector ) ) {
		elements.unshift( root );
	}

	return elements;
};

export const initFadeInAnimations = ( root = document ) => {
	if ( prefersReducedMotion() ) {
		gsap.set( getElements( root, '[data-fade-in]' ), {
			clearProps: 'all',
			opacity: 1,
			y: 0,
		} );

		return;
	}

	getElements( root, '[data-fade-in]' ).forEach( item => {
		if ( item.dataset.fadeInInitialized ) {
			return;
		}

		item.dataset.fadeInInitialized = 'true';

		const [delay = 0, duration = 2.4, start = 85] = data( item, 'fadeIn' );

		gsap.to( item, {
			opacity: 1,
			ease: "power2.out",
			duration,
			delay,
			scrollTrigger: {
				trigger: item,
				start: `top ${start}%`,
				toggleActions: "play none none none",
			}
		} );
	} );

	ScrollTrigger.refresh();
};

document.addEventListener( 'DOMContentLoaded', function() {
	if ( prefersReducedMotion() ) {
		gsap.set( '[data-appear], [data-fade-in], [data-fade-up], [data-animation]', {
			clearProps: 'all',
			opacity: 1,
			y: 0,
		} );

		return;
	}

	/**
	 * [data-appear].
	 * DELAY | STAGGER | DURATION | START
	 */
	const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();

	fontsReady.then( () => {
		document.querySelectorAll( "[data-appear]" ).forEach( ( item ) => {
			const [delay = 0, stagger = 0.08, duration = 2.4, start = 85] =
				data( item, "appear" );

			SplitText.create( item, {
				type: "words,lines",
				linesClass: "line",
				mask: "lines",
				autoSplit: true,
				onSplit: ( self ) => {
					gsap.set( item, { opacity: 1 } );

					const tween = gsap.from( self.lines, {
						yPercent: 80,
						opacity: 0,
						duration,
						stagger,
						delay,
						ease: "power4.out",
						scrollTrigger: {
							trigger: item,
							start: `top ${start}%`,
							toggleActions: "play none none none",
						},
					} );

					item._tween = tween;
				},
			} );
		} );
	} );

	/**
	 * [data-fade-in].
	 * DELAY | DURATION | START
	 */
	initFadeInAnimations();

	/**
	 * [data-fade-up].
	 * DELAY | DURATION | START | DISTANCE
	 */
	document.querySelectorAll( '[data-fade-up]' ).forEach( item => {
		const [delay = 0, duration = 1.8, start = 85, distance = 56] = data( item, 'fadeUp' );

		gsap.fromTo(
			item,
			{
				opacity: 0,
				y: distance,
			},
			{
				opacity: 1,
				y: 0,
				ease: "power2.out",
				duration,
				delay,
				scrollTrigger: {
					trigger: item,
					start: `top ${start}%`,
					toggleActions: "play none none none",
				}
			}
		);
	} );

	/**
	 * [data-slide-left]
	 * DELAY | DURATION | START | DISTANCE
	 */
	document.querySelectorAll('[data-slide-left]').forEach(item => {
		const [delay = 0, duration = 1.8, start = 85, distance = 80] = data(item, 'slideLeft');

		gsap.fromTo(
			item,
			{
				x: distance, // right se start
				opacity: 0,
			},
			{
				x: 0,
				opacity: 1,
				duration,
				delay,
				ease: "power2.out",
				scrollTrigger: {
					trigger: item,
					start: `top ${start}%`,
					toggleActions: "play none none none",
				},
			}
		);
	});

	/**
	 * [data-animation]
	 * SPEED
	 */

	document.querySelectorAll( "[data-animation]" ).forEach( ( img ) => {
		const y = parseFloat( img.dataset.animation ) || 100;
		const duration = parseFloat( img.dataset.duration ) || 8;
		const delay = parseFloat( img.dataset.delay ) || 0;

		gsap.fromTo(
			img,
			{
				y: - y,
				opacity: 0,
			},
			{
				y: 0,
				opacity: 1,
				duration,
				delay,
				ease: "power2.out",
				scrollTrigger: {
					trigger: img,
					start: "top 70%",
					toggleActions: "play none none none",
					invalidateOnRefresh: true,
				},
			}
		);
	} );

	const refresh = debounce( () => {
		ScrollTrigger.refresh();
	}, 150 );

	window.addEventListener( "load", () => {
		ScrollTrigger.refresh();
	} );

	window.addEventListener( "resize", refresh );
} );

function data( item, key ) {
	return (item.dataset[ key ] || "").split( "|" ).map( v => v.trim() === "" ? undefined : Number( v ) );
}

function debounce( callback, wait ) {
	let timeout;

	return function() {
		clearTimeout( timeout );
		timeout = setTimeout( callback, wait );
	};
}
